import React, { Component } from "react";
import { observable, action } from "mobx";
import { observer, inject } from "mobx-react";
import {
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormInput,
  FormSelect,
  FormTextarea,
  InputGroup,
  Badge
} from "shards-react";
import { toast } from "react-toastify";
import FeatherIcon from "feather-icons-react";
import moment from "moment";

import PageTitle from "../../../components/common/PageTitle";
import { QNA_CATEGORIES, getQnACategory, QNA_DEFAULT_CATEGORY, QNA_CATEGORY_FALLBACK_LABEL } from "../Board/qnaCategories";

const emptyForm = {
  _id: null,
  title: '',
  content: '',
  inquiryType: QNA_DEFAULT_CATEGORY,
  isPinned: false,
  status: 'published' // 'published' | 'hidden'
};

/**
 * QnA 관리 (관리자/SuperAdmin)
 * - 관리자(슈퍼어드민) 가 자주 묻는 질문/답변을 등록/수정/삭제.
 * - boardName: 'qna' 로 구분. 기존 Board 컬렉션 재사용.
 * - 상태: published(공개), hidden(비공개). 고객(/qna) 페이지는 published 만 노출.
 * - 검색: title + content 키워드 OR 매칭(서버).
 */
@inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))
@inject(({ board }) => ({
  getBoardList: board.getBoardList,
  updateBoard: board.updateBoard,
  removeBoard: board.removeBoard
}))
@observer
class QnAList extends Component {

  @observable store = {};
  @observable teamUser = {};
  @observable isLogin = false;
  @observable isLoading = false;

  @observable list = { docs: [], totalDocs: 0 };
  @observable keyword = '';
  @observable expandedId = null;
  @observable categoryFilter = ''; // '' = 전체, 그 외는 QNA_CATEGORIES.value

  @observable listParams = {
    page: 1,
    limit: 100,
    searchParams: {
      boardName: 'qna',
      status: 'all'   // 관리자는 published/hidden 모두 보임
    }
  };

  // 등록/수정 모달
  @observable isFormOpen = false;
  @observable form = { ...emptyForm };

  // 삭제 확인 모달
  @observable isConfirmDeleteOpen = false;
  @observable deleteTargetId = null;

  componentDidMount() {
    this.init();
  }

  @action
  init = () => {
    if (this.props.isLogin) {
      this.isLogin = true;
      this.teamUser = this.props.me;
      this.store = (this.props.me && this.props.me.store) || {};
      this.getList();
    }
  };

  // ===== List =====
  @action
  getList = async () => {
    this.isLoading = true;
    try {
      const res = await this.props.getBoardList(this.listParams);
      this.list = res || { docs: [], totalDocs: 0 };
    } catch (e) {
      console.error('[QnAList] getList error:', e);
      this.list = { docs: [], totalDocs: 0 };
    }
    this.isLoading = false;
  };

  @action
  onChangeKeyword = (event) => {
    this.keyword = event.target.value;
  };

  @action
  onSubmitSearch = () => {
    this.listParams.searchParams.keyword = (this.keyword || '').trim();
    this.listParams.page = 1;
    this.expandedId = null;
    this.getList();
  };

  @action
  onClearKeyword = () => {
    this.keyword = '';
    this.listParams.searchParams.keyword = '';
    this.listParams.page = 1;
    this.expandedId = null;
    this.getList();
  };

  @action
  onChangeCategoryFilter = (value) => {
    this.categoryFilter = value || '';
    if (this.categoryFilter) {
      this.listParams.searchParams.inquiryType = this.categoryFilter;
    } else {
      delete this.listParams.searchParams.inquiryType;
    }
    this.listParams.page = 1;
    this.expandedId = null;
    this.getList();
  };

  handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.onSubmitSearch();
    }
  };

  @action
  toggleExpand = (id) => {
    this.expandedId = this.expandedId === id ? null : id;
  };

  // ===== Create/Edit Modal =====
  @action
  openCreateModal = () => {
    this.form = { ...emptyForm };
    this.isFormOpen = true;
  };

  @action
  openEditModal = (item) => {
    this.form = {
      _id: item._id,
      title: item.title || '',
      content: item.content || '',
      inquiryType: item.inquiryType || QNA_DEFAULT_CATEGORY,
      isPinned: !!item.isPinned,
      status: item.status || 'published'
    };
    this.isFormOpen = true;
  };

  @action
  closeFormModal = () => {
    this.isFormOpen = false;
    this.form = { ...emptyForm };
  };

  @action
  onChangeFormField = (key, value) => {
    this.form[key] = value;
  };

  @action
  submitForm = async () => {
    const f = this.form;
    if (!(f.title + '').trim()) {
      toast.error('질문(제목)을 입력해주세요.');
      return;
    }
    if (!(f.content + '').trim()) {
      toast.error('답변(내용)을 입력해주세요.');
      return;
    }

    const params = {
      boardName: 'qna',
      store_id: this.store && this.store._id,
      shopNo: (this.teamUser && this.teamUser.selShopNo) || 1,
      title: (f.title + '').trim(),
      content: f.content,
      inquiryType: f.inquiryType || QNA_DEFAULT_CATEGORY,
      status: f.status || 'published',
      visibility: 'public',
      isSecret: false,
      locked: false,
      isPinned: !!f.isPinned,
      notifyAuthor: false
    };
    if (f._id) params.id = f._id;

    try {
      const res = await this.props.updateBoard(params);
      if (res && res.errors && res.errors.length > 0) {
        toast.error(res.errors[0].message);
        return;
      }
      toast.success(f._id ? 'QnA가 수정되었습니다.' : 'QnA가 등록되었습니다.');
      this.closeFormModal();
      this.getList();
    } catch (e) {
      console.error('[QnAList] submitForm error:', e);
      toast.error('저장 중 오류가 발생했습니다.');
    }
  };

  // ===== Quick toggles (pin/status) =====
  @action
  togglePin = async (item) => {
    const params = {
      id: item._id,
      store_id: item.store_id && item.store_id._id,
      shopNo: item.shopNo || 1,
      boardName: 'qna',
      isPinned: !item.isPinned,
      notifyAuthor: false
    };
    try {
      await this.props.updateBoard(params);
      toast.success(item.isPinned ? '고정이 해제되었습니다.' : '상단에 고정되었습니다.');
      this.getList();
    } catch (e) {
      toast.error('고정 변경 중 오류가 발생했습니다.');
    }
  };

  @action
  toggleStatus = async (item) => {
    const next = item.status === 'published' ? 'hidden' : 'published';
    const params = {
      id: item._id,
      store_id: item.store_id && item.store_id._id,
      shopNo: item.shopNo || 1,
      boardName: 'qna',
      status: next,
      notifyAuthor: false
    };
    try {
      await this.props.updateBoard(params);
      toast.success(next === 'published' ? '공개로 전환했습니다.' : '비공개로 전환했습니다.');
      this.getList();
    } catch (e) {
      toast.error('상태 변경 중 오류가 발생했습니다.');
    }
  };

  // ===== Delete =====
  @action
  openConfirmDelete = (id) => {
    this.deleteTargetId = id;
    this.isConfirmDeleteOpen = true;
  };

  @action
  closeConfirmDelete = () => {
    this.deleteTargetId = null;
    this.isConfirmDeleteOpen = false;
  };

  @action
  confirmDelete = async () => {
    if (!this.deleteTargetId) return;
    try {
      const res = await this.props.removeBoard({ id: this.deleteTargetId });
      if (res && res.errors && res.errors.length > 0) {
        toast.error(res.errors[0].message);
        return;
      }
      toast.success('삭제되었습니다.');
      this.closeConfirmDelete();
      this.getList();
    } catch (e) {
      toast.error('삭제 중 오류가 발생했습니다.');
    }
  };

  render() {
    const docs = (this.list && this.list.docs) || [];
    const hasKeyword = !!(this.listParams.searchParams.keyword || '').trim();

    return (
      <div className="main-content">
        <PageTitle />

        {/* ===== 검색 + 등록 ===== */}
        <Card small style={{ padding: '16px 18px', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <InputGroup style={{ flex: 1, minWidth: 220 }}>
              <FormInput
                placeholder="제목·내용 검색"
                value={this.keyword}
                onChange={this.onChangeKeyword}
                onKeyDown={this.handleKeyDown}
                style={{ fontSize: 14 }}
              />
            </InputGroup>
            <Button theme="primary" onClick={this.onSubmitSearch} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <FeatherIcon icon="search" size="14" /> 검색
            </Button>
            {hasKeyword && (
              <Button theme="light" onClick={this.onClearKeyword} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <FeatherIcon icon="x" size="14" /> 초기화
              </Button>
            )}
            <div style={{ flex: '0 0 auto', marginLeft: 'auto' }}>
              <Button theme="success" onClick={this.openCreateModal} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <FeatherIcon icon="plus" size="14" /> 새 QnA 등록
              </Button>
            </div>
          </div>
          {/* 카테고리 필터 칩 — 전체 + QNA_CATEGORIES */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
            <span style={{ fontSize: 12, color: '#6B7280', marginRight: 4 }}>분류:</span>
            {[{ value: '', label: '전체' }, ...QNA_CATEGORIES].map((c) => {
              const active = this.categoryFilter === c.value;
              return (
                <button
                  key={c.value || 'all'}
                  type="button"
                  onClick={() => this.onChangeCategoryFilter(c.value)}
                  style={{
                    padding: '4px 10px', fontSize: 12, fontWeight: active ? 700 : 500,
                    border: '1px solid ' + (active ? '#1F2937' : '#E5E7EB'),
                    background: active ? '#1F2937' : '#FFFFFF',
                    color: active ? '#FFFFFF' : '#374151',
                    borderRadius: 999, cursor: 'pointer'
                  }}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </Card>

        {/* ===== 결과 카운트 ===== */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, padding: '0 4px' }}>
          <div style={{ fontSize: 13, color: '#6B7280' }}>
            총 <b style={{ color: '#1F2937' }}>{this.list.totalDocs || 0}</b>건
            {hasKeyword && (
              <span style={{ marginLeft: 8, color: '#9CA3AF' }}>— "{this.listParams.searchParams.keyword}" 검색</span>
            )}
          </div>
          {this.isLoading && <span style={{ fontSize: 12, color: '#9CA3AF' }}>불러오는 중…</span>}
        </div>

        {/* ===== 리스트 (아코디언 + 액션) ===== */}
        <Card small>
          <CardBody style={{ padding: 0 }}>
            {docs.length === 0 ? (
              <div style={{ padding: '60px 20px', textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>
                {hasKeyword ? '검색 결과가 없습니다.' : '등록된 QnA가 없습니다. 우측 상단 [새 QnA 등록] 으로 시작하세요.'}
              </div>
            ) : (
              docs.map((item, i) => {
                const id = item._id;
                const isOpen = this.expandedId === id;
                const isHidden = item.status === 'hidden';
                return (
                  <div
                    key={id || i}
                    style={{
                      borderBottom: i === docs.length - 1 ? 'none' : '1px solid #F3F4F6',
                      background: isOpen ? '#FAFBFC' : '#FFFFFF',
                      opacity: isHidden ? 0.55 : 1
                    }}
                  >
                    {/* 헤더 — 클릭 시 펼침 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px' }}>
                      <button
                        type="button"
                        onClick={() => this.toggleExpand(id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0,
                          background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0
                        }}
                      >
                        {item.isPinned && (
                          <FeatherIcon icon="bookmark" size="14" style={{ color: '#F59E0B', flexShrink: 0 }} />
                        )}
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 22, height: 22, borderRadius: '50%',
                          background: '#EFF6FF', color: '#2563EB',
                          fontSize: 12, fontWeight: 700, flexShrink: 0
                        }}>Q</span>
                        {/* 분류 배지 */}
                        {(() => {
                          const meta = getQnACategory(item.inquiryType);
                          if (!meta && !item.inquiryType) return null;
                          const bg = meta ? meta.bg : '#F3F4F6';
                          const color = meta ? meta.color : '#6B7280';
                          const label = meta ? meta.label : QNA_CATEGORY_FALLBACK_LABEL;
                          return (
                            <span style={{
                              padding: '2px 8px', fontSize: 11, fontWeight: 600,
                              background: bg, color, borderRadius: 4, flexShrink: 0
                            }}>{label}</span>
                          );
                        })()}
                        <span style={{
                          flex: 1, minWidth: 0,
                          fontSize: 14, color: '#1F2937', fontWeight: isOpen ? 700 : 500,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }}>
                          {item.title || '(제목 없음)'}
                        </span>
                        {isHidden && (
                          <Badge theme="secondary" style={{ flexShrink: 0, fontSize: 10 }}>비공개</Badge>
                        )}
                        <FeatherIcon icon={isOpen ? 'chevron-up' : 'chevron-down'} size="16" style={{ color: '#9CA3AF', flexShrink: 0 }} />
                      </button>
                      {/* 액션 버튼 그룹 */}
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                        <Button
                          size="sm" theme={item.isPinned ? 'warning' : 'light'}
                          onClick={() => this.togglePin(item)}
                          title={item.isPinned ? '고정 해제' : '상단 고정'}
                          style={{ padding: '4px 8px', fontSize: 11 }}
                        >
                          <FeatherIcon icon="bookmark" size="11" />
                        </Button>
                        <Button
                          size="sm" theme={isHidden ? 'secondary' : 'light'}
                          onClick={() => this.toggleStatus(item)}
                          title={isHidden ? '공개로 전환' : '비공개로 전환'}
                          style={{ padding: '4px 8px', fontSize: 11 }}
                        >
                          <FeatherIcon icon={isHidden ? 'eye-off' : 'eye'} size="11" />
                        </Button>
                        <Button
                          size="sm" theme="light" onClick={() => this.openEditModal(item)}
                          title="수정" style={{ padding: '4px 8px', fontSize: 11 }}
                        >
                          <FeatherIcon icon="edit-2" size="11" />
                        </Button>
                        <Button
                          size="sm" theme="danger" onClick={() => this.openConfirmDelete(id)}
                          title="삭제" style={{ padding: '4px 8px', fontSize: 11 }}
                        >
                          <FeatherIcon icon="trash-2" size="11" />
                        </Button>
                      </div>
                    </div>

                    {/* 답변 펼침 */}
                    {isOpen && (
                      <div style={{ padding: '0 20px 18px 54px' }}>
                        <div style={{
                          display: 'flex', alignItems: 'flex-start', gap: 12,
                          background: '#F9FAFB', borderLeft: '3px solid #10B981',
                          borderRadius: 6, padding: '14px 16px'
                        }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 22, height: 22, borderRadius: '50%',
                            background: '#D1FAE5', color: '#047857',
                            fontSize: 12, fontWeight: 700, flexShrink: 0, marginTop: 1
                          }}>A</span>
                          <div style={{
                            flex: 1, minWidth: 0, fontSize: 13.5, lineHeight: 1.7,
                            color: '#374151', whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                          }}>
                            {item.content || '(내용 없음)'}
                          </div>
                        </div>
                        <div style={{ marginTop: 8, fontSize: 11, color: '#9CA3AF', textAlign: 'right' }}>
                          등록: {item.createdAt ? moment(item.createdAt).format('YYYY.MM.DD HH:mm') : '-'}
                          {item.updatedAt && item.updatedAt !== item.createdAt && (
                            <> · 수정: {moment(item.updatedAt).format('YYYY.MM.DD HH:mm')}</>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </CardBody>
        </Card>

        {/* ===== 등록/수정 모달 ===== */}
        <Modal open={this.isFormOpen} toggle={() => {}} size="lg">
          <ModalHeader toggle={this.closeFormModal}>
            {this.form._id ? 'QnA 수정' : '새 QnA 등록'}
          </ModalHeader>
          <ModalBody style={{ padding: '20px' }}>
            {/* 분류 */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 6 }}>분류</label>
              <FormSelect
                value={this.form.inquiryType || QNA_DEFAULT_CATEGORY}
                onChange={(e) => this.onChangeFormField('inquiryType', e.target.value)}
              >
                {QNA_CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </FormSelect>
            </div>
            {/* 질문 */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 6 }}>질문 (Q)</label>
              <FormInput
                placeholder="고객이 자주 묻는 질문을 입력하세요"
                value={this.form.title}
                onChange={(e) => this.onChangeFormField('title', e.target.value)}
              />
            </div>
            {/* 답변 */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#6B7280', marginBottom: 6 }}>답변 (A)</label>
              <FormTextarea
                rows={10}
                placeholder="답변 내용을 입력하세요. 줄바꿈은 그대로 표시됩니다."
                value={this.form.content}
                onChange={(e) => this.onChangeFormField('content', e.target.value)}
                style={{ fontSize: 13.5, lineHeight: 1.6 }}
              />
            </div>
            {/* 옵션 */}
            <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginTop: 18 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!this.form.isPinned}
                  onChange={(e) => this.onChangeFormField('isPinned', e.target.checked)}
                />
                <FeatherIcon icon="bookmark" size="13" style={{ color: this.form.isPinned ? '#F59E0B' : '#9CA3AF' }} />
                상단 고정
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={this.form.status === 'published'}
                  onChange={(e) => this.onChangeFormField('status', e.target.checked ? 'published' : 'hidden')}
                />
                <FeatherIcon icon={this.form.status === 'published' ? 'eye' : 'eye-off'} size="13" style={{ color: this.form.status === 'published' ? '#10B981' : '#9CA3AF' }} />
                공개 (체크 해제 시 비공개)
              </label>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button theme="light" onClick={this.closeFormModal}>취소</Button>
            <Button theme="primary" onClick={this.submitForm}>
              {this.form._id ? '수정 저장' : '등록'}
            </Button>
          </ModalFooter>
        </Modal>

        {/* ===== 삭제 확인 모달 ===== */}
        <Modal open={this.isConfirmDeleteOpen} toggle={() => {}} size="sm">
          <ModalHeader toggle={this.closeConfirmDelete}>삭제 확인</ModalHeader>
          <ModalBody style={{ padding: '20px', fontSize: 14 }}>
            선택한 QnA를 정말로 삭제하시겠습니까?<br />
            <span style={{ color: '#991B1B', fontSize: 12 }}>이 작업은 되돌릴 수 없습니다.</span>
          </ModalBody>
          <ModalFooter>
            <Button theme="light" onClick={this.closeConfirmDelete}>취소</Button>
            <Button theme="danger" onClick={this.confirmDelete}>삭제</Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }
}

export default QnAList;
